import React, { useMemo, useState } from 'react';
import { BsFillCircleFill, BsInfoCircle } from 'react-icons/bs';
import { Box, Button, Checkbox, Flex, HStack, Icon, IconButton, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { inAppText } from '../../constants/inAppText';
import colors from '../../styles/theme/foundation/colors';

import CloneOptionsStep from './components/CloneOptionsStep';
import DocumentCard from './components/DocumentCard';
import PolicyCard from './components/PolicyCard';
import RecordSearchList from './components/RecordSearchList';

const CloningTray = ({
  policies,
  documents,
  job,
  isLoading,
  handleSubmit,
  handleExit,
  currentRecord,
  companyId,
  insuredId,
  insuredName
}) => {
  const componentPolicies = useMemo(() => policies, [policies]);
  const componentDocuments = useMemo(() => documents, [documents]);
  const [currentStep, setCurrentStep] = useState(1);
  const [policyObject, setPolicyObject] = useState(componentPolicies.reduce((obj, p) => {
    obj[p.id] = false;

    return obj;
  }, {}));
  const [documentObject, setDocumentObject] = useState(componentDocuments.reduce((obj, d) => {
    obj[d.id] = false;

    return obj;
  }, {}));
  const [recordObject, setRecordObject] = useState({});
  const [copyRequirementStatuses, setCopyRequirementStatuses] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const maxStep = 4;
  const minStep = 1;

  const backButtonCopy = useMemo(() => {
    switch (currentStep) {
      case 1: return 'Cancel';
      default: return 'Back';
    }
  }, [currentStep]);

  const forwardButtonCopy = useMemo(() => {
    switch (currentStep) {
      case 4: return 'Save';
      default: return 'Next';
    }
  }, [currentStep]);

  const handlePreviousButtonClick = () => {
    const resultStep = currentStep - 1;

    if (resultStep >= minStep) {
      setIsConfirming(false);
      setCurrentStep(resultStep);
    } else {
      handleExit();
    }
  };

  const countSelected = selection => Object.values(selection).filter(Boolean).length;

  const confirmationCopy = useMemo(() => inAppText.cloningTray.confirmModal.getDescription({
    policyCount: countSelected(policyObject),
    documentCount: countSelected(documentObject),
    recordCount: countSelected(recordObject),
    copyRequirementStatuses
  }), [policyObject, documentObject, recordObject, copyRequirementStatuses]);

  const submit = () => handleSubmit(recordObject, policyObject, documentObject, { copyRequirementStatuses });

  const handleNextButtonClick = () => {
    const resultStep = currentStep + 1;

    if (resultStep <= maxStep) {
      setCurrentStep(resultStep);
    } else {
      setIsConfirming(true);
    }
  };

  const currentList = useMemo(() => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <HStack justifyContent="space-between" mb={4}>
              <Text textStyle="displayXxs">Select Policy(s) to Copy</Text>
              <Checkbox
                isChecked={Object.values(policyObject).every(val => val)}
                onChange={e => {
                  const objectCopy = policyObject;

                  Object.keys(objectCopy).forEach(pid => {
                    objectCopy[pid] = e.target.checked;
                  });

                  setPolicyObject({ ...objectCopy });
                }}
              >
                Select All
              </Checkbox>
            </HStack>

            <Box>
              {!componentPolicies.length && (
                <Text mt={8} textAlign="center">
                  None available
                </Text>
              )}

              {componentPolicies.map(policy => (
                <HStack key={policy.id} pb={4} pt={2}>
                  <Checkbox
                    id={`clone-policy-${policy.id}`}
                    isChecked={policyObject[policy.id]}
                    value={policy.id}
                    onChange={e => setPolicyObject({ ...policyObject, [policy.id]: e.target.checked })}
                  />
                  <Box
                    cursor="pointer"
                    onClick={() => {
                      const policyCheckbox = document.getElementById(`clone-policy-${policy.id}`);

                      policyCheckbox.dispatchEvent(new MouseEvent('click', { bubbles: true }));
                    }}
                  >
                    <PolicyCard policy={policy} />
                  </Box>
                </HStack>
              ))}
            </Box>
          </>
        );
      case 2:
        return (
          <>
            <HStack justifyContent="space-between" mb={4}>
              <Text textStyle="displayXxs">Select Document(s) to Copy</Text>
              <Checkbox
                isChecked={Object.values(documentObject).every(val => val)}
                onChange={e => {
                  const objectCopy = documentObject;

                  Object.keys(objectCopy).forEach(pid => {
                    objectCopy[pid] = e.target.checked;
                  });

                  setDocumentObject({ ...objectCopy });
                }}
              >
                Select All
              </Checkbox>
            </HStack>

            <Box>
              {!componentDocuments.length && (
                <Text mt={8} textAlign="center">
                  None available
                </Text>
              )}

              {componentDocuments.map(doc => (
                <HStack key={doc.id} pb={4} pt={2}>
                  <Checkbox
                    id={`clone-document-${doc.id}`}
                    isChecked={documentObject[doc.id]}
                    value={doc.id}
                    onChange={e => setDocumentObject({ ...documentObject, [doc.id]: e.target.checked })}
                  />
                  <Box
                    cursor="pointer"
                    onClick={() => {
                      const documentCheckbox = document.getElementById(`clone-document-${doc.id}`);

                      documentCheckbox.dispatchEvent(new MouseEvent('click', { bubbles: true }));
                    }}
                  >
                    <DocumentCard document={doc} />
                  </Box>
                </HStack>
              ))}
            </Box>
          </>
        );
      case 3:
        return (
          <RecordSearchList
            companyId={companyId}
            excludeRecordId={currentRecord?.id}
            insuredId={insuredId}
            insuredName={insuredName}
            recordObject={recordObject}
            setRecordObject={setRecordObject}
          />
        );
      case 4:
        return (
          <CloneOptionsStep
            copyRequirementStatuses={copyRequirementStatuses}
            selectedDocumentCount={Object.values(documentObject).filter(Boolean).length}
            selectedPolicyCount={Object.values(policyObject).filter(Boolean).length}
            selectedRecordCount={Object.values(recordObject).filter(Boolean).length}
            setCopyRequirementStatuses={setCopyRequirementStatuses}
          />
        );
      default:
        return [];
    }
  }, [currentStep, policyObject, documentObject, recordObject, copyRequirementStatuses, companyId, currentRecord?.id, insuredId, insuredName]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100vh"
      justifyContent="space-between"
      overflow="auto"
      p={8}
    >
      <Box flexBasis="20%">
        <HStack justifyContent="space-between">
          <Box>
            <HStack pb={4}>
              <Text textStyle="displayXxs">Copy to New Record</Text>
              <Tooltip
                borderRadius="md"
                fontSize="xs"
                label="Select the policies from the current Record to be cloned to additional Records."
                placement="top"
              >
                <IconButton
                  aria-label="info-circle"
                  icon={<Icon as={BsInfoCircle} boxSize="18px" />}
                  size="xs"
                  variant="textGrey"
                />
              </Tooltip>
            </HStack>

            <Text color={colors.greyScale['700']} textStyle="bodyMd500">Current Record</Text>
            <HStack>
              <Text color={colors.greyScale['700']} textStyle="bodyMd">Insured:</Text>
              <Text color={colors.greyScale['700']} textStyle="bodyMd500">{insuredName || 'N/A'}</Text>
            </HStack>
            <HStack>
              <Text color={colors.greyScale['700']} textStyle="bodyMd">Record Number:</Text>
              <Text color={colors.greyScale['700']} textStyle="bodyMd500">{currentRecord?.contractNumber ?? 'N/A'}</Text>
            </HStack>
            <HStack>
              <Text color={colors.greyScale['700']} textStyle="bodyMd">Project Name:</Text>
              <Text color={colors.greyScale['700']} textStyle="bodyMd500">{job?.jobName ?? 'N/A'}</Text>
            </HStack>
            <HStack>
              <Text color={colors.greyScale['700']} textStyle="bodyMd">Project Number:</Text>
              <Text color={colors.greyScale['700']} textStyle="bodyMd500">{job?.jobNumber ? job.jobNumber : 'N/A'}</Text>
            </HStack>
          </Box>

          <Box px={8}>
            <HStack>
              <Icon
                as={BsFillCircleFill}
                boxSize="12px"
                color={currentStep === 1 ? colors.primaryScale['600'] : colors.primaryScale['200']}
              />
              <Icon
                as={BsFillCircleFill}
                boxSize="12px"
                color={currentStep === 2 ? colors.primaryScale['600'] : colors.primaryScale['200']}
              />
              <Icon
                as={BsFillCircleFill}
                boxSize="12px"
                color={currentStep === 3 ? colors.primaryScale['600'] : colors.primaryScale['200']}
              />
              <Icon
                as={BsFillCircleFill}
                boxSize="12px"
                color={currentStep === 4 ? colors.primaryScale['600'] : colors.primaryScale['200']}
              />
            </HStack>
          </Box>
        </HStack>
      </Box>

      <Box flexBasis="75%" overflowY="auto">
        {currentList}
      </Box>

      <Box
        alignItems="center"
        flexBasis="5%"
      >
        {isConfirming ? (
          <Flex
            alignItems="center"
            data-cy="clone-confirmation"
            flexWrap="wrap"
            gap={4}
            justifyContent="space-between"
          >
            <Box flex="1 1 240px">
              <Text color={colors.greyScale['800']} textStyle="bodyMd500">
                {inAppText.cloningTray.confirmModal.title}
              </Text>
              <Text color={colors.greyScale['700']} textStyle="bodySm">
                {confirmationCopy}
              </Text>
            </Box>

            <HStack spacing={3}>
              <Button
                isDisabled={isLoading}
                variant="secondary"
                onClick={() => setIsConfirming(false)}
              >
                {inAppText.cloningTray.confirmModal.cancelButtonLabel}
              </Button>
              <Button
                data-cy="clone-confirm-submit"
                isLoading={isLoading}
                onClick={submit}
              >
                {inAppText.cloningTray.confirmModal.confirmButtonLabel}
              </Button>
            </HStack>
          </Flex>
        ) : (
          <HStack alignItems="center" justifyContent="space-between">
            <Button
              isDisabled={isLoading}
              variant="secondary"
              onClick={handlePreviousButtonClick}
            >
              {backButtonCopy}
            </Button>
            <Button
              isLoading={isLoading}
              onClick={handleNextButtonClick}
            >
              {forwardButtonCopy}
            </Button>
          </HStack>
        )}
      </Box>
    </Box>
  );
};

CloningTray.propTypes = {
  job: PropTypes.shape({
    requirementContractNumber: PropTypes.string,
    jobName: PropTypes.string,
    jobNumber: PropTypes.string
  }),
  documents: PropTypes.arrayOf(PropTypes.shape({
    insuranceDocumentType: PropTypes.shape({ displayName: PropTypes.string }),
    displayName: PropTypes.string,
    expiresAt: PropTypes.string
  })),
  policies: PropTypes.arrayOf(PropTypes.shape({
    effectiveAt: PropTypes.string,
    expiresAt: PropTypes.string,
    number: PropTypes.string,
    insurancePolicyType: PropTypes.shape({ displayName: PropTypes.string })
  })),
  isLoading: PropTypes.bool,
  handleSubmit: PropTypes.func,
  handleExit: PropTypes.func,
  currentRecord: PropTypes.shape({
    id: PropTypes.number,
    contractNumber: PropTypes.string
  }),
  companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  insuredId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  insuredName: PropTypes.string
};

export default CloningTray;
